import { useState, useEffect } from "react";
import { Menu, X, Building2 } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import LanguageSwitcher from "@/components/site/LanguageSwitcher";

export const Header = () => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const links = [
    { href: "#services", label: t.nav.services, key: "services" },
    { href: "#calculator", label: t.nav.calculator, key: "calculator" },
    { href: "#about", label: t.nav.about, key: "about" },
    { href: "#contact", label: t.nav.contact, key: "contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-background/75 border-b border-border/60"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between gap-4">
        <a
          href="#top"
          data-testid="brand-link"
          aria-label="DBR Invest a.s."
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium rounded-full hover:bg-foreground transition-colors shrink-0"
        >
          <Building2 className="w-4 h-4" strokeWidth={2.2} />
          <span className="font-heading tracking-tight">
            DBR&nbsp;Invest&nbsp;a.s.
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.key}
              href={l.href}
              data-testid={`nav-${l.key}`}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-primary group-hover:w-full transition-all duration-300" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <a
            href="#contact"
            data-testid="cta-header-contact"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium rounded-full hover:bg-foreground transition-colors"
          >
            {t.nav.cta}
          </a>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden p-2 rounded-sm hover:bg-muted transition-colors"
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-border/60 bg-background/95 backdrop-blur-xl">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.key}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.key}`}
                className="text-base text-foreground py-1"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-cta-contact"
              className="mt-2 inline-flex justify-center items-center px-5 py-3 bg-primary text-primary-foreground rounded-full text-sm font-medium"
            >
              {t.nav.cta}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
